import { Device } from "@/features/devices/types";
import {
  DownloadSpeedTestSnapshot,
  formatDownloadMbps,
  formatSpeedTestTimestamp,
  NOT_REPORTED,
} from "@/features/devices/deviceDetailDisplay";
import { isDeviceFleetCommandsEnabled } from "@/features/devices/devicePreview";

export const TEST_DOWNLOAD_SPEED_COMMAND = "test_download_speed";
export const SPEED_TEST_ENDPOINT_LABEL = "Cloudflare";
export const SPEED_TEST_DISCLAIMER =
  "Measures device-to-Cloudflare throughput. Not a guaranteed ISP plan speed.";

export type DownloadSpeedTestUiState =
  | "idle"
  | "running"
  | "successful"
  | "failed"
  | "timed_out"
  | "unsupported";

export function lastSuccessfulMbps(
  snapshot: DownloadSpeedTestSnapshot | null | undefined,
  download: string | number | null | undefined
): number | null {
  const fromSnap = snapshot?.mbps;
  if (typeof fromSnap === "number" && Number.isFinite(fromSnap) && fromSnap > 0) {
    return fromSnap;
  }
  if (typeof download === "number" && Number.isFinite(download) && download > 0) {
    return download;
  }
  return null;
}

export function formatDownloadSpeedDisplay(
  snapshot: DownloadSpeedTestSnapshot | null | undefined,
  download: string | number | null | undefined
): string {
  return formatDownloadMbps(lastSuccessfulMbps(snapshot, download));
}

export function formatUploadSpeedDisplay(
  upload: string | number | null | undefined
): string {
  return formatDownloadMbps(upload);
}

export function lastTestedLabel(
  snapshot: DownloadSpeedTestSnapshot | null | undefined
): string | null {
  const when = formatSpeedTestTimestamp(snapshot?.measuredAt || null);
  return when ? `Last tested ${when}` : null;
}

/**
 * last_seen_at is not a connectivity heartbeat.
 * Fleet fills it with lastSeenAt || androidIdUpdatedAt || updatedAt.
 * Heartbeat remains independently "Not reported" until a verified reporter exists.
 * androidIdUpdatedAt (credential bind) can be weeks stale on an online Player.
 * Do not use that timestamp to disable Test Speed.
 */
export function isDeviceSpeedTestOffline(
  device: Pick<Device, "activation_status" | "status">
): boolean {
  return device.activation_status === "deactivated";
}

export function speedTestDisabledReason(args: {
  device: Pick<Device, "last_seen_at" | "status" | "inventory_status" | "activation_status">;
  snapshot?: DownloadSpeedTestSnapshot | null;
  uiState: DownloadSpeedTestUiState;
  commandsEnabled?: boolean;
}): string | null {
  const commandsEnabled =
    args.commandsEnabled ?? isDeviceFleetCommandsEnabled();
  if (!commandsEnabled) return "unavailable";
  if (args.uiState === "unsupported") return "unsupported";
  if (args.uiState === "running" || args.snapshot?.inFlight) return "busy";
  if (args.snapshot?.cooldownActive) return "cooldown";
  const inventory = args.device.inventory_status;
  if (inventory === "unclaimed" || inventory === "in-warehouse") return "busy";
  if (isDeviceSpeedTestOffline(args.device)) return "offline";
  return null;
}

export function speedTestStatusLabel(state: DownloadSpeedTestUiState): string | null {
  switch (state) {
    case "running":
      return "Running";
    case "successful":
      return "Successful";
    case "failed":
      return "Failed";
    case "timed_out":
      return "Timed Out";
    case "unsupported":
      return "Unsupported";
    default:
      return null;
  }
}

export function speedTestEndpointLabel(
  snapshot: DownloadSpeedTestSnapshot | null | undefined
): string {
  const host = snapshot?.endpointHost;
  if (host && host.includes("cloudflare")) return SPEED_TEST_ENDPOINT_LABEL;
  return host || SPEED_TEST_ENDPOINT_LABEL;
}

export { NOT_REPORTED };
